// zoomPan.ts — pure zoom/pan maths for the media viewer.
//
// Kept free of React and the DOM so the fiddly parts (clamping, recentring, the
// fit -> actual transition) are unit-testable on their own. This is where the bugs would
// otherwise live.
//
// Two named states rather than a continuous zoom slider:
//   FIT    — the whole image is visible, scaled down to the viewport.
//   ACTUAL — the image at its UPLOADED pixel resolution (1:1), pannable.
// "Actual" is the state people actually want from a photo viewer: they tap to inspect
// detail, and detail means real pixels.

export type ZoomMode = 'fit' | 'actual';

export interface Size {
  width: number;
  height: number;
}

export interface PanState {
  mode: ZoomMode;
  /** Pan offset in CSS px, applied only in `actual` mode. */
  x: number;
  y: number;
}

export const INITIAL: PanState = { mode: 'fit', x: 0, y: 0 };

/** The scale at which the whole image fits inside the viewport (never upscales past 1). */
export function fitScale(natural: Size, viewport: Size): number {
  if (!natural.width || !natural.height || !viewport.width || !viewport.height) return 1;
  return Math.min(viewport.width / natural.width, viewport.height / natural.height, 1);
}

/** True when the image is larger than the viewport, i.e. zooming to 1:1 reveals more detail. */
export function canZoom(natural: Size, viewport: Size): boolean {
  return fitScale(natural, viewport) < 1;
}

/**
 * Largest allowed pan offset on each axis. Zero when the image is smaller than the
 * viewport on that axis, which keeps it pinned to the centre instead of drifting.
 */
export function panBounds(natural: Size, viewport: Size): { maxX: number; maxY: number } {
  return {
    maxX: Math.max(0, (natural.width - viewport.width) / 2),
    maxY: Math.max(0, (natural.height - viewport.height) / 2),
  };
}

export function clampPan(state: PanState, natural: Size, viewport: Size): PanState {
  const { maxX, maxY } = panBounds(natural, viewport);
  return {
    ...state,
    x: Math.min(maxX, Math.max(-maxX, state.x)),
    y: Math.min(maxY, Math.max(-maxY, state.y)),
  };
}

/**
 * Toggle between fit and actual.
 *
 * `origin` is the pointer position relative to the viewport centre, so zooming in on the
 * top-left of a photo keeps that spot roughly under the cursor rather than jumping to the
 * middle. Returning to fit always recentres.
 */
export function toggleZoom(
  state: PanState,
  natural: Size,
  viewport: Size,
  origin: { x: number; y: number } = { x: 0, y: 0 },
): PanState {
  if (state.mode === 'actual') return { ...INITIAL };
  if (!canZoom(natural, viewport)) return { ...INITIAL };
  const scale = fitScale(natural, viewport);
  // Where the clicked point lands once the image is at 1:1, then move it back to centre.
  const next: PanState = {
    mode: 'actual',
    x: -(origin.x / scale - origin.x),
    y: -(origin.y / scale - origin.y),
  };
  return clampPan(next, natural, viewport);
}

/** Apply a drag delta while panning in actual mode. */
export function panBy(
  state: PanState,
  dx: number,
  dy: number,
  natural: Size,
  viewport: Size,
): PanState {
  if (state.mode !== 'actual') return state;
  return clampPan({ ...state, x: state.x + dx, y: state.y + dy }, natural, viewport);
}

/** Step to another image; zoom/pan always resets so each photo opens fit. */
export function stepIndex(index: number, delta: number, total: number): number {
  if (total <= 0) return 0;
  return (index + delta + total) % total;
}
